import { useTraderText } from '../context/TraderLanguageContext';
/**
 * Trader Registration Page
 * Dark theme registration with all fields matching ZrpGlobal style
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTraderAuth } from '../context/TraderAuthContext';
import { useTraderLanguage } from '../context/TraderLanguageContext';
import { useTraderTheme } from '../context/TraderThemeContext';
import { Mail, Lock, Eye, EyeOff, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import {TenantBrand} from '../../branding/BrandingContext';

// Country codes with flags
const countryCodes = [
  { code: '+90', country: 'TR', flag: '🇹🇷' },
  { code: '+1', country: 'US', flag: '🇺🇸' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+49', country: 'DE', flag: '🇩🇪' },
  { code: '+33', country: 'FR', flag: '🇫🇷' },
  { code: '+34', country: 'ES', flag: '🇪🇸' },
  { code: '+39', country: 'IT', flag: '🇮🇹' },
  { code: '+31', country: 'NL', flag: '🇳🇱' },
  { code: '+357', country: 'CY', flag: '🇨🇾' },
  { code: '+971', country: 'AE', flag: '🇦🇪' },
  { code: '+966', country: 'SA', flag: '🇸🇦' },
  { code: '+7', country: 'RU', flag: '🇷🇺' },
  { code: '+86', country: 'CN', flag: '🇨🇳' },
  { code: '+81', country: 'JP', flag: '🇯🇵' },
  { code: '+82', country: 'KR', flag: '🇰🇷' },
  { code: '+91', country: 'IN', flag: '🇮🇳' },
  { code: '+55', country: 'BR', flag: '🇧🇷' },
  { code: '+52', country: 'MX', flag: '🇲🇽' },
  { code: '+61', country: 'AU', flag: '🇦🇺' },
  { code: '+64', country: 'NZ', flag: '🇳🇿' },
];

const TraderRegister = () => {
  const tx = useTraderText();
  const navigate = useNavigate();
  const { register } = useTraderAuth();
  const { t, language, toggleLanguage } = useTraderLanguage();
  const { isDark } = useTraderTheme();
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    countryCode: '+90',
    phone: '',
    registrationCode: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const selectedCountry = countryCodes.find(c => c.code === formData.countryCode);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password) {
      toast.error(tx(t('validation.required')));
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error(tx(t('validation.passwordMin')));
      return;
    }
    
    if (!agreedToTerms) {
      toast.error(tx('Please agree to the terms and conditions'));
      return;
    }
    
    setLoading(true);
    const result = await register({
      first_name: formData.firstName,
      last_name: formData.lastName,
      email: formData.email,
      password: formData.password,
      phone: `${formData.countryCode}${formData.phone}`,
      registration_code: formData.registrationCode || null,
    });
    setLoading(false);
    
    if (result.success) {
      toast.success(tx('Registration successful!'));
      navigate('/trader');
    } else {
      toast.error(tx(result.error));
    }
  };

  const inputClass = `w-full px-4 py-4 rounded-xl border focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
    isDark 
      ? 'bg-[#1a2332] border-gray-700 text-white placeholder-gray-500' 
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
  }`;

  return (
    <div className={`min-h-screen flex items-center justify-center px-4 py-8 ${
      isDark ? 'bg-[#0d1421]' : 'bg-gray-100'
    }`}>
      {/* Language Toggle - Top Right */}
      <button
        onClick={toggleLanguage}
        data-testid="trader-register-language-toggle"
        aria-label={tx('Language')}
        className="fixed top-4 right-4 flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors z-50"
      >
        <span className="text-2xl">{language === 'en' ? '🇬🇧' : '🇹🇷'}</span>
        <span className={isDark ? 'text-white' : 'text-gray-800'}>{tx(language.toUpperCase())}</span>
      </button>

      <div className="w-full max-w-md">
        {/* Registration Code Button */}
        {!showCodeInput ? (
          <button
            onClick={() => setShowCodeInput(true)}
            className="mb-6 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-full transition-colors"
          >
            {t('auth.insertCode')}
          </button>
        ) : (
          <div className="mb-6">
            <input
              type="text"
              value={formData.registrationCode}
              onChange={(e) => handleChange('registrationCode', e.target.value)}
              className={inputClass}
              placeholder={tx("Enter registration code")}
            />
          </div>
        )}

        {/* Title */}
        <div className="tenant-login-brand"><TenantBrand id="trader-registration-brand"/></div>
        <h1 className={`text-3xl font-bold mb-8 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {t('auth.registration')}
        </h1>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Name Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-700'}`}>
                {t('auth.firstName')}
              </label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => handleChange('firstName', e.target.value)}
                className={inputClass}
                data-testid="register-firstname-input"
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-700'}`}>
                {t('auth.lastName')}
              </label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => handleChange('lastName', e.target.value)}
                className={inputClass}
                data-testid="register-lastname-input"
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-700'}`}>
              {t('auth.email')}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Mail size={20} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
              </div>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className={`${inputClass} pl-12`}
                data-testid="register-email-input"
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-700'}`}>
              {t('auth.password')}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Lock size={20} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
              </div>
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                className={`${inputClass} pl-12 pr-12`}
                data-testid="register-password-input"
                minLength={6}
                maxLength={72}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-4 flex items-center"
              >
                {showPassword ? (
                  <EyeOff size={20} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
                ) : (
                  <Eye size={20} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
                )}
              </button>
            </div>
          </div>

          {/* Phone */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-700'}`}>
                {t('auth.code')}
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                  className={`w-full px-4 py-4 rounded-xl border flex items-center justify-between ${
                    isDark 
                      ? 'bg-[#1a2332] border-gray-700 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <span>{selectedCountry?.flag} {selectedCountry?.code}</span>
                  <ChevronDown size={16} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
                </button>
                
                {showCountryDropdown && (
                  <div className={`absolute top-full left-0 right-0 mt-1 rounded-xl border max-h-60 overflow-y-auto z-50 ${
                    isDark 
                      ? 'bg-[#1a2332] border-gray-700' 
                      : 'bg-white border-gray-300'
                  }`}>
                    {countryCodes.map((c) => (
                      <button
                        key={c.code}
                        type="button"
                        onClick={() => {
                          handleChange('countryCode', c.code);
                          setShowCountryDropdown(false);
                        }}
                        className={`w-full px-4 py-3 text-left flex items-center gap-2 ${
                          isDark 
                            ? 'hover:bg-gray-700 text-white' 
                            : 'hover:bg-gray-100 text-gray-900'
                        } ${formData.countryCode === c.code ? 'bg-blue-500/20' : ''}`}
                      >
                        <span>{c.flag}</span>
                        <span>{c.code}</span>
                        <span className="text-gray-500 text-sm">({c.country})</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="col-span-2">
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-700'}`}>
                {t('auth.phone')}
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value.replace(/\D/g, ''))}
                className={inputClass}
                placeholder={t('auth.phoneNumber')}
                data-testid="register-phone-input"
              />
            </div>
          </div>

          {/* Register Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="register-submit-btn"
          >
            {loading ? t('common.loading') : t('auth.registerButton')}
          </button>
        </form>

        {/* Terms Agreement */}
        <div className="mt-6 flex items-start gap-3">
          <input
            type="checkbox"
            checked={agreedToTerms}
            onChange={(e) => setAgreedToTerms(e.target.checked)}
            className="mt-1 w-4 h-4 rounded border-gray-600 text-blue-500 focus:ring-blue-500"
            data-testid="register-terms-checkbox"
          />
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {t('auth.termsText')}{' '}
            <Link to="/trader/terms" className="text-blue-400 hover:text-blue-300">
              {t('auth.termsLink')}
            </Link>
            ,{' '}
            <Link to="/trader/privacy" className="text-blue-400 hover:text-blue-300">
              {t('auth.privacyLink')}
            </Link>
            {t('auth.andAllDocs')}
          </p>
        </div>

        {/* Risk Warning */}
        <p className={`mt-6 text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
          {t('auth.riskWarning')}{' '}
          <Link to="/trader/risk-disclosure" className="text-blue-400 hover:text-blue-300">
            {t('auth.learnMore')}
          </Link>
        </p>

        {/* Login Link */}
        <p className={`mt-6 text-center ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          {t('auth.haveAccount')}{' '}
          <Link to="/trader/login" className="text-blue-400 hover:text-blue-300 font-semibold">
            {t('auth.login')}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default TraderRegister;
